import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';

interface User {
	id: number;
	email: string;
	role: 'admin' | 'staff';
	is_dev?: boolean;
	first_time_login?: boolean;
}

interface Business {
	id: string;
	name: string;
	logo_url?: string;
	street?: string;
	city?: string;
	state?: string;
	zip?: string;
	country?: string;
	phone?: string;
	email?: string;
	website?: string;
	brand_color?: string;
	default_terms?: string;
	footer_msg?: string;
	late_fee_type?: string;
	late_fee_value?: number | null;
	late_fee_days?: number | null;
	owner_user_id: number;
	// Private to this user; never sent back to server except via nickname endpoints
	nickname?: string | null;
}

interface AuthContextType {
	user: User | null;
	currentBusiness: Business | null;
	businesses: Business[];
	token: string | null;
	csrfToken: string | null;
	isCookieMode: boolean;
	API_BASE_URL: string;
	login: (email: string, password: string) => Promise<void>;
	logout: () => void;
	setCurrentBusiness: (businessId: string) => void;
	register: (email: string, password: string, confirmPassword: string, betaCode: string, tosAccepted: boolean, privacyPolicyAccepted: boolean) => Promise<void>;
	isLoading: boolean;
	refreshBusinesses: () => Promise<void>;
	fetchCsrfToken: () => Promise<string | null>;
	entitlements: string[];
	isPlatformDev: boolean;
	isBusinessSessionSynced: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};

interface AuthProviderProps {
	children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [user, setUser] = useState<User | null>(null);
	const [currentBusiness, setCurrentBusiness] = useState<Business | null>(null);
	const [businesses, setBusinesses] = useState<Business[]>([]);
	const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
	const [csrfToken, setCsrfToken] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [entitlements, setEntitlements] = useState<string[]>([]);
	const [isBusinessSessionSynced, setIsBusinessSessionSynced] = useState<boolean>(false);
	
	// Check if cookie mode is enabled
	const isCookieMode = process.env.REACT_APP_AUTH_IN_COOKIE === '1';
	
	// API base URL for production
	const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

	// Prevent concurrent/duplicate CSRF fetches
	const csrfInflightRef = useRef<Promise<string | null> | null>(null);
	const sessionBusinessIdRef = useRef<string | null>(null);
	
	// Function to fetch CSRF token from /auth/me
	const fetchCsrfToken = useCallback(async () => {
		// Return cached token if present
		if (csrfToken) return csrfToken;
		// Return inflight promise if already fetching
		if (csrfInflightRef.current) return csrfInflightRef.current;
		
		const p = (async () => {
			try {
				const response = await fetch(`${API_BASE_URL}/auth/me`, {
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
				});
				if (response.ok) {
					const data = await response.json();
					if (data?.user) {
						// Only update if user actually changes to avoid triggering effects
						setUser((prev) => {
							const next = data.user as User;
							if (prev && prev.id === next.id && prev.email === next.email && prev.role === next.role && prev.is_dev === next.is_dev) {
								return prev;
							}
							return next;
						});
					}
					if (Array.isArray(data?.entitlements)) {
						setEntitlements(data.entitlements);
					}
					// Capture session business id for syncing with businesses list
					if (data?.business?.id) {
						sessionBusinessIdRef.current = data.business.id as string;
						// If businesses already loaded, reflect immediately
						if (businesses.length > 0) {
							const match = businesses.find(b => b.id === sessionBusinessIdRef.current!);
							if (match) setCurrentBusiness(match);
						}
					}
					if (data.csrf) {
						setCsrfToken(data.csrf);
						(window as any).__csrf = data.csrf;
						return data.csrf as string;
					}
					return null;
				} else {
					return null;
				}
			} catch {
				return null;
			} finally {
				csrfInflightRef.current = null;
			}
		})();
		csrfInflightRef.current = p;
		return p;
	}, [API_BASE_URL, csrfToken, businesses]);
	
	// Restore session on mount
	useEffect(() => {
		let cancelled = false;
		const bootstrap = async () => {
			if (isCookieMode) {
				// In cookie mode, fetch /auth/me to hydrate user+csrf before rendering routes
				try { await fetchCsrfToken(); } catch {}
				if (!cancelled) setIsLoading(false);
				return;
			}
			// Token mode: restore from localStorage as before
			const storedToken = localStorage.getItem('token');
			if (storedToken && !user) {
				try {
					const decoded: any = jwtDecode(storedToken);
					setUser({ id: decoded.id, email: decoded.email, role: decoded.role });
				} catch (e) {
					setUser(null);
					setToken(null);
					localStorage.removeItem('token');
				}
			}
			if (!cancelled) setIsLoading(false);
		};
		bootstrap();
		return () => { cancelled = true; };
	}, [isCookieMode, fetchCsrfToken]);

	// Fetch CSRF token once when authenticated and missing
	useEffect(() => {
		if (user && (isCookieMode || token) && !csrfToken) {
			fetchCsrfToken();
		}
	}, [user, token, isCookieMode, csrfToken, fetchCsrfToken]);

	// Fetch businesses when user is authenticated
	useEffect(() => {
		// In cookie mode, we only need user to be set
		// In token mode, we need both token and user
		if (user && (isCookieMode || token)) {
			fetchBusinesses();
		}
	}, [token, user, isCookieMode]);

	// Do not persist currentBusinessId locally; rely on session via /auth/me
	useEffect(() => {
		if (businesses.length > 0) {
			// Sync to session-selected business if present, but do not override if already matching
			if (sessionBusinessIdRef.current) {
				const match = businesses.find(b => b.id === sessionBusinessIdRef.current);
				if (match && currentBusiness?.id !== match.id) {
					setCurrentBusiness(match);
					setIsBusinessSessionSynced(true);
					return;
				}
			}
			// If no session business yet and only one business exists, auto-select via /auth/select-business
			if (!sessionBusinessIdRef.current && businesses.length === 1) {
				(async () => {
					try {
						const only = businesses[0];
						// Ensure CSRF present for unsafe POST
						const csrf = await fetchCsrfToken();
						const res = await fetch(`${API_BASE_URL}/auth/select-business`, {
							method: 'POST',
							credentials: 'include',
							headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
							body: JSON.stringify({ businessId: only.id })
						});
						if (res.ok) {
							sessionBusinessIdRef.current = only.id;
							setCurrentBusiness(only);
							setIsBusinessSessionSynced(true);
							await fetchCsrfToken();
						}
					} catch {}
				})();
			}
		}
	}, [businesses, API_BASE_URL, fetchCsrfToken, currentBusiness?.id]);

	const fetchBusinesses = async () => {
		try {
			console.log('[AuthContext] Fetching businesses...');
			const requestOptions: RequestInit = {
				credentials: 'include', // Always include credentials for cross-origin requests
				headers: {
					'Content-Type': 'application/json',
				},
			};
			
			// Only add Authorization header if not in cookie mode
			if (!isCookieMode && token) {
				requestOptions.headers = {
					...requestOptions.headers,
					'Authorization': `Bearer ${token}`,
				};
			}
			
			const response = await fetch(`${API_BASE_URL}/businesses`, requestOptions);

			if (response.ok) {
				const data = await response.json();
				console.log('[AuthContext] Fetched businesses:', data);
				// Fetch per-business nickname in parallel
				const withNicknames = await Promise.all(
					data.map(async (b: Business) => {
						try {
							const nicknameOptions: RequestInit = {
								credentials: 'include', // Always include credentials for cross-origin requests
								headers: {
									'Content-Type': 'application/json',
								},
							};
							
							if (!isCookieMode && token) {
								nicknameOptions.headers = {
									...nicknameOptions.headers,
									'Authorization': `Bearer ${token}`,
								};
							}
							
							const r = await fetch(`${API_BASE_URL}/businesses/${b.id}/nickname`, nicknameOptions);
							if (r.ok) {
								const n = await r.json();
								return { ...b, nickname: n.nickname ?? null } as Business;
							}
						} catch (_) {}
						return { ...b, nickname: null } as Business;
					})
				);
				setBusinesses(withNicknames);
			}
		} catch (error) {
			console.error('Error fetching businesses:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const refreshBusinesses = async () => {
		await fetchBusinesses();
	};

	const login = async (email: string, password: string) => {
		try {
			// Reset any stale session business pointer before starting a new login
			sessionBusinessIdRef.current = null;
			const response = await fetch(`${API_BASE_URL}/auth/login`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				credentials: 'include',
				body: JSON.stringify({ email, password }),
			});

			if (!response.ok) {
				const errorBody = await response.json().catch(() => ({}));
				const err: any = new Error(errorBody?.error || 'Login failed');
				if (errorBody?.code) err.code = errorBody.code;
				throw err;
			}

			const data = await response.json();
			
			if (isCookieMode) {
				setToken(null);
				setUser(data.user);
				// Hydrate CSRF and entitlements from /auth/me
				const csrf = await fetchCsrfToken();
				if (csrf) setCsrfToken(csrf);
			} else {
				setToken(data.token);
				setUser(data.user);
				localStorage.setItem('token', data.token);
				const csrf = await fetchCsrfToken();
				if (csrf) setCsrfToken(csrf);
			}
		} catch (error) {
			throw error;
		}
	};

	const register = async (email: string, password: string, confirmPassword: string, betaCode: string, tosAccepted: boolean, privacyPolicyAccepted: boolean) => {
		try {
			const response = await fetch(`${API_BASE_URL}/users/register`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ email, password, confirmPassword, betaCode, tosAccepted, privacyPolicyAccepted }),
			});

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || 'Registration failed');
			}

			// Success; let the caller decide where to route next (e.g., /check-email)
			return await response.json();
		} catch (error) {
			throw error;
		}
	};

	const logout = async () => {
		try {
			if (isCookieMode) {
				await fetch(`${API_BASE_URL}/auth/logout`, {
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
				});
			}
		} catch (error) {
			console.error('[AuthContext] Error during logout:', error);
		} finally {
			setUser(null);
			setCurrentBusiness(null);
			setBusinesses([]);
			setToken(null);
			setCsrfToken(null);
			(window as any).__csrf = null;
			localStorage.removeItem('token');
			localStorage.removeItem('currentBusinessId');
			setEntitlements([]);
			// Clear any stale session-selected business id so re-login can auto-select when only one business exists
			sessionBusinessIdRef.current = null;
			setIsBusinessSessionSynced(false);
		}
	};

	const handleBusinessChange = (businessId: string) => {
		console.log('[AuthContext] Changing business to:', businessId);
		const business = businesses.find((b: Business) => b.id === businessId);
		if (business) {
			console.log('[AuthContext] Setting current business to:', business.name);
			// Optimistically reflect selection locally and in session ref to avoid race with sync effects
			const previousSessionId = sessionBusinessIdRef.current;
			sessionBusinessIdRef.current = businessId;
			setCurrentBusiness(business);
			setIsBusinessSessionSynced(false);
			// Inform server of the selected business to populate session entitlements
			(async () => {
				try {
					// Ensure CSRF present for unsafe POST
					const csrf = await fetchCsrfToken();
					const res = await fetch(`${API_BASE_URL}/auth/select-business`, {
						method: 'POST',
						credentials: 'include',
						headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
						body: JSON.stringify({ businessId })
					});
					if (res.ok) {
						const data = await res.json().catch(() => ({}));
						if (Array.isArray(data?.entitlements)) {
							setEntitlements(data.entitlements);
						}
						// Refresh CSRF and user/auth state from /auth/me (deduped)
						await fetchCsrfToken();
						setIsBusinessSessionSynced(true);
					} else {
						console.error('[AuthContext] /auth/select-business failed with status:', res.status);
						// Revert session ref and local selection on failure
						sessionBusinessIdRef.current = previousSessionId;
						if (previousSessionId) {
							const prev = businesses.find(b => b.id === previousSessionId);
							if (prev) setCurrentBusiness(prev);
						}
						setIsBusinessSessionSynced(true);
					}
				} catch (err) {
					console.error('[AuthContext] Failed to select business:', err);
					// Revert session ref and local selection on error
					sessionBusinessIdRef.current = previousSessionId;
					if (previousSessionId) {
						const prev = businesses.find(b => b.id === previousSessionId);
						if (prev) setCurrentBusiness(prev);
					}
					setIsBusinessSessionSynced(true);
				}
			})();
		} else {
			console.error('[AuthContext] Business not found:', businessId);
		}
	};

	const isPlatformDev = Array.isArray(entitlements) && (entitlements.includes('platform.dev') || entitlements.includes('admin.*')) || !!user?.is_dev;

	const value: AuthContextType = {
		user,
		currentBusiness,
		businesses,
		token,
		csrfToken,
		isCookieMode,
		API_BASE_URL,
		login,
		logout,
		setCurrentBusiness: handleBusinessChange,
		register,
		isLoading,
		refreshBusinesses,
		fetchCsrfToken,
		entitlements,
		isPlatformDev,
		isBusinessSessionSynced
	};

	return (
		<AuthContext.Provider value={value}>
			{children}
		</AuthContext.Provider>
	);
}; 